/**
 * CollapsibleSectionAtom.tsx
 *
 * A collapsible section component designed for form sections.
 * Features:
 * - Expand/collapse toggle
 * - Optional step number
 * - Error state indicator
 * - Header actions slot
 */

import React, { useState, useCallback, useRef, useEffect, useId } from 'react';
import IconOrGlyph from '../Glyph/IconOrGlyph';
import './CollapsibleSectionAtom.scss';

export interface CollapsibleSectionAtomProps {
  /** Section title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional step number (e.g., 1, 2, 3) */
  stepNumber?: number;
  /** Whether the section is expanded by default */
  defaultExpanded?: boolean;
  /** Controlled expanded state */
  expanded?: boolean;
  /** Callback when expansion state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Whether the section has validation errors */
  hasErrors?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /**
   * Renders `errorMessage` as visible header text (under the title) while `hasErrors`, and
   * describes the group by it. Without it the message is only a tooltip on the error icon, so
   * the failure is signalled by colour and icon alone. Defaults off.
   */
  showErrorMessage?: boolean;
  /** Whether the section is disabled/non-interactive */
  disabled?: boolean;
  /** Optional actions to show in the header */
  headerActions?: React.ReactNode;
  /** Section content */
  children: React.ReactNode;
  /** Additional CSS class names */
  className?: string;
  /** Test ID for automation */
  'data-testid'?: string;
  /**
   * Container id for the prototype-parity walker (`data-container`). A section
   * that renders a prototype block names that block's implementation id here so
   * every datum inside it can be located in a capture and diffed for
   * containment and order (PAR-5). Additive; absent means "not a contract
   * container".
   */
  'data-container'?: string;
  /** DOM id on the root element, for in-page anchor navigation (e.g. a section index). */
  anchorId?: string;
  /** PrimeIcons class (e.g. `pi pi-tag`) or Iconify name (e.g. `lucide:tag`), rendered before the title. */
  icon?: string;
  /** Rendered immediately after the title (e.g. a ghost/proposed tag). Additive — omitted renders unchanged. */
  titleAdornment?: React.ReactNode;
  /**
   * When `false`, the section renders permanently expanded with no toggle affordance and the
   * header click fires `onHeaderClick` instead of a collapse toggle — the rail pattern's `TBlock`
   * (`type-rail/trail-composer.jsx:36-48`), whose head selects the block rather than collapsing
   * it. Defaults to `true` (existing collapse behavior, unchanged for current consumers).
   */
  collapsible?: boolean;
  /**
   * Fired when the header is clicked while `collapsible` is `false`. Ignored when `collapsible`
   * is `true` (or omitted) — those consumers keep the toggle-only header they already have.
   */
  onHeaderClick?: () => void;
  /** Renders `data-sel` on the root when true — this section is the one the rail is editing. */
  selected?: boolean;
  /** Renders `data-ghost` on the root when true — an agent proposal owns this section's content. */
  ghost?: boolean;
  /** Renders `data-bad` on the root when true — this section has a validation failure. */
  bad?: boolean;
}

const CollapsibleSectionAtom: React.FC<CollapsibleSectionAtomProps> = ({
  title,
  subtitle,
  stepNumber,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  hasErrors = false,
  errorMessage,
  showErrorMessage = false,
  disabled = false,
  headerActions,
  children,
  className,
  'data-testid': testId,
  'data-container': container,
  anchorId,
  icon,
  titleAdornment,
  collapsible = true,
  onHeaderClick,
  selected,
  ghost,
  bad,
}) => {
  // Use controlled state if provided, otherwise internal state
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = collapsible ? (controlledExpanded !== undefined ? controlledExpanded : internalExpanded) : true;
  const interactive = collapsible || !!onHeaderClick;
  const idBase = useId();
  const titleId = `${idBase}-title`;
  const errorId = `${idBase}-error`;
  const visibleError = showErrorMessage && hasErrors && !!errorMessage;

  // The body snaps to size and its content fades/slides in (ux-craft M3: transform and
  // opacity only — a measured `height` transition animated layout and needed a timer to
  // return to `auto`). The `data-expanded` attribute drives the stylesheet's states.
  const contentRef = useRef<HTMLDivElement>(null);

  // Collapsed content is hidden from assistive tech (`aria-hidden`) AND must be out of the
  // tab order: `aria-hidden` alone left its inputs focusable, so a keyboard user tabbed into
  // fields a screen reader could not see (axe `aria-hidden-focus`). `inert` does both; set as
  // a DOM property because React 18's typings do not know the attribute.
  useEffect(() => {
    if (contentRef.current) contentRef.current.inert = !isExpanded;
  }, [isExpanded]);

  // Handle toggle
  const handleToggle = useCallback(() => {
    if (disabled) return;

    if (!collapsible) {
      onHeaderClick?.();
      return;
    }

    const newExpanded = !isExpanded;
    setInternalExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  }, [collapsible, disabled, isExpanded, onExpandedChange, onHeaderClick]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    },
    [disabled, handleToggle]
  );

  // Build CSS classes
  const containerClasses = [
    'collapsible-section',
    isExpanded && 'collapsible-section--expanded',
    hasErrors && 'collapsible-section--has-errors',
    disabled && 'collapsible-section--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // A fieldset named by its title: a group, and a disabled section is disabled natively —
    // every control inside it is out of reach, and the text it dims reads as part of an
    // inactive group (WCAG 1.4.3 exempts inactive components).
    <fieldset
      id={anchorId}
      className={containerClasses}
      data-testid={testId}
      data-container={container}
      data-ui={container ? 'section' : undefined}
      data-sel={selected ? '1' : undefined}
      data-ghost={ghost ? '1' : undefined}
      data-bad={bad ? '1' : undefined}
      aria-labelledby={titleId}
      aria-describedby={visibleError ? errorId : undefined}
      disabled={disabled}
    >
      {/* Header */}
      {/* A non-collapsible header with nothing to fire is plain content: giving it `role="button"`
          and a tab stop would put a focusable no-op in the keyboard order. When it does select
          (the rail pattern), `aria-pressed` carries the selection the `data-sel` colour shows. */}
      <div
        className="collapsible-section__header"
        role={interactive ? 'button' : undefined}
        // A static (non-interactive) header still needs to accept *programmatic* focus — a
        // section index's "jump to this block" (`SphereTypeComposerOrganism.focusSection`) calls
        // `.focus()` on it after scrolling. `-1` keeps it out of the Tab order (it does nothing on
        // click or Enter here), matching a plain-content header that happens to be a focus target.
        tabIndex={interactive ? (disabled ? undefined : 0) : -1}
        aria-expanded={collapsible ? isExpanded : undefined}
        aria-pressed={!collapsible && interactive ? !!selected : undefined}
        aria-disabled={interactive ? disabled : undefined}
        onClick={interactive ? handleToggle : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
      >
        <div className="collapsible-section__header-content">
          {icon && <IconOrGlyph icon={icon} className="collapsible-section__icon" />}
          {/* Step number */}
          {stepNumber !== undefined && (
            <span className="collapsible-section__step-number">
              ({stepNumber})
            </span>
          )}

          {/* Title and subtitle */}
          <div className="collapsible-section__title-wrapper">
            {/* The adornment sits INLINE after the title (a ghost tag reads `h3 · [proposed]`
                on one line); only the subtitle drops beneath. */}
            <span className="collapsible-section__title-row">
              <span id={titleId} className="collapsible-section__title">{title}</span>
              {titleAdornment}
            </span>
            {subtitle && (
              <span className="collapsible-section__subtitle">{subtitle}</span>
            )}
            {visibleError && (
              <span id={errorId} className="collapsible-section__error-message">{errorMessage}</span>
            )}
          </div>

          {/* Error indicator */}
          {hasErrors && (
            <span className="collapsible-section__error-indicator" title={errorMessage}>
              <i className="pi pi-exclamation-circle" />
            </span>
          )}
        </div>

        {/* Header actions and toggle */}
        <div className="collapsible-section__header-actions">
          {headerActions && (
            <div
              className="collapsible-section__actions"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              {headerActions}
            </div>
          )}
          {collapsible && (
            <span className="collapsible-section__toggle-icon">
              <i className={`pi ${isExpanded ? 'pi-minus' : 'pi-plus'}`} />
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        ref={contentRef}
        className="collapsible-section__content"
        data-expanded={isExpanded ? 'true' : 'false'}
        aria-hidden={!isExpanded}
      >
        <div className="collapsible-section__content-inner">{children}</div>
      </div>
    </fieldset>
  );
};

CollapsibleSectionAtom.displayName = 'CollapsibleSectionAtom';

export default CollapsibleSectionAtom;
